import { useCallback, useEffect, useMemo, useState } from "react";
import { useAccount, useSignMessage } from "wagmi";
import { zeroAddress, type Address } from "viem";
import { env } from "../config/env";
import {
  buildBindMessage,
  buildClaimMessage,
  buildInviteLink,
  getStoredReferrerOrZero,
  readReferrerFromUrl,
  storeReferrer,
} from "../lib/referral";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ReferralStats {
  totalInvited: number;
  totalRewarded: string;
  pendingAmount: string;
  claimableAmount: string;
}

export interface ReferralClaimable {
  id: number;
  invitee: Address;
  rewardAmount: string;
  eligibleTimestamp: number;
}

export interface ReferralHistoryItem {
  id: number;
  invitee: Address;
  rewardAmount: string;
  claimedAt: number;
  txHash?: string;
}

export interface ClaimResponse {
  ok: boolean;
  txHash?: string;
  error?: string;
}

export interface UseReferralResult {
  enabled: boolean;
  isConnected: boolean;
  address: Address | undefined;
  storedReferrer: Address;
  inviteLink: string;
  stats: ReferralStats | null;
  claimable: ReferralClaimable[];
  history: ReferralHistoryItem[];
  loading: boolean;
  error: string | null;
  bind: (inviter: Address) => Promise<void>;
  claim: (rewardIds: number[]) => Promise<ClaimResponse>;
  refresh: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Internal fetch helper — keeps error handling consistent across endpoints
// ---------------------------------------------------------------------------

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { error?: string };
      detail = body?.error ?? "";
    } catch {
      // swallow: best-effort detail extraction
    }
    throw new Error(detail || `Request failed: ${response.status}`);
  }
  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// Disabled-shape returned when the feature flag is off
// ---------------------------------------------------------------------------

function disabledResult(): UseReferralResult {
  return {
    enabled: false,
    isConnected: false,
    address: undefined,
    storedReferrer: zeroAddress,
    inviteLink: "",
    stats: null,
    claimable: [],
    history: [],
    loading: false,
    error: null,
    bind: async () => {
      throw new Error("Referral feature disabled");
    },
    claim: async () => ({ ok: false, error: "Referral feature disabled" }),
    refresh: async () => {
      /* no-op */
    },
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useReferral(): UseReferralResult {
  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();

  const [storedReferrer, setStoredReferrer] = useState<Address>(getStoredReferrerOrZero());
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [claimable, setClaimable] = useState<ReferralClaimable[]>([]);
  const [history, setHistory] = useState<ReferralHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiBase = env.referralApiUrl;
  const enabled = env.referralEnabled;

  // -------------------------------------------------------------------------
  // 1. Capture ?ref= on mount (skip self-referral, skip if disabled)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    const ref = readReferrerFromUrl(window.location.href);
    if (!ref) return;
    if (address && ref.toLowerCase() === address.toLowerCase()) return;
    if (storeReferrer(ref, address ?? undefined)) {
      setStoredReferrer(ref);
    }
  }, [enabled, address]);

  // -------------------------------------------------------------------------
  // 2. Fetch stats/claimable/history when address changes
  // -------------------------------------------------------------------------
  const refresh = useCallback(async (): Promise<void> => {
    if (!enabled || !address || !apiBase) return;
    setLoading(true);
    setError(null);
    try {
      const [nextStats, nextClaimable, nextHistory] = await Promise.all([
        fetchJson<ReferralStats>(`${apiBase}/referral/stats/${address}`),
        fetchJson<ReferralClaimable[]>(`${apiBase}/referral/claimable/${address}`),
        fetchJson<ReferralHistoryItem[]>(`${apiBase}/referral/history/${address}`),
      ]);
      setStats(nextStats);
      setClaimable(Array.isArray(nextClaimable) ? nextClaimable : []);
      setHistory(Array.isArray(nextHistory) ? nextHistory : []);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Failed to load referral data");
    } finally {
      setLoading(false);
    }
  }, [enabled, address, apiBase]);

  useEffect(() => {
    if (!enabled) {
      setStats(null);
      setClaimable([]);
      setHistory([]);
      return;
    }
    void refresh();
  }, [enabled, refresh]);

  // -------------------------------------------------------------------------
  // 3. Bind action — signs and POSTs to /referral/bind
  // -------------------------------------------------------------------------
  const bind = useCallback(
    async (inviter: Address): Promise<void> => {
      if (!enabled) throw new Error("Referral feature disabled");
      if (!address) throw new Error("Wallet not connected");
      if (!apiBase) throw new Error("Referral API not configured");
      if (inviter.toLowerCase() === address.toLowerCase()) {
        throw new Error("Cannot bind to self");
      }

      const message = buildBindMessage(address, inviter);
      const signature = await signMessageAsync({ message });

      await fetchJson<{ ok: boolean }>(`${apiBase}/referral/bind`, {
        method: "POST",
        body: JSON.stringify({ invitee: address, inviter, signature }),
      });

      // Persist locally so the UI can show the inviter even before the keeper
      // confirms the binding.
      storeReferrer(inviter, address);
      setStoredReferrer(inviter);
    },
    [enabled, address, apiBase, signMessageAsync],
  );

  // -------------------------------------------------------------------------
  // 4. Claim action — signs claim message and POSTs reward ids
  // -------------------------------------------------------------------------
  const claim = useCallback(
    async (rewardIds: number[]): Promise<ClaimResponse> => {
      if (!enabled) return { ok: false, error: "Referral feature disabled" };
      if (!address) throw new Error("Wallet not connected");
      if (!apiBase) throw new Error("Referral API not configured");
      if (rewardIds.length === 0) {
        return { ok: false, error: "No rewards selected" };
      }

      const timestamp = Math.floor(Date.now() / 1000);
      const message = buildClaimMessage(address, timestamp);
      const signature = await signMessageAsync({ message });

      const response = await fetchJson<ClaimResponse>(`${apiBase}/referral/claim`, {
        method: "POST",
        body: JSON.stringify({ address, timestamp, signature, rewardIds }),
      });

      // Best-effort refresh; failure here shouldn't shadow the claim result.
      void refresh();
      return response;
    },
    [enabled, address, apiBase, signMessageAsync, refresh],
  );

  // -------------------------------------------------------------------------
  // 5. Derived invite link
  // -------------------------------------------------------------------------
  const inviteLink = useMemo(() => {
    if (!enabled || !address) return "";
    if (typeof window === "undefined") return "";
    return buildInviteLink(window.location.origin, address);
  }, [enabled, address]);

  if (!enabled) return disabledResult();

  return {
    enabled,
    isConnected,
    address,
    storedReferrer,
    inviteLink,
    stats,
    claimable,
    history,
    loading,
    error,
    bind,
    claim,
    refresh,
  };
}
